/**
 * shared.ts — Tipos e helpers do módulo de Autorizações.
 */
import { formatarDinheiro } from '@/lib/numero';

export type StatusAutorizacao =
  | 'pendente'
  | 'autorizada'
  | 'negada'
  | 'cancelada'
  /** A autorizada cujo ciclo fechou: virou pedido de venda, ou não virou
   *  venda. Só a partir de `autorizada` (migration 078, ficha #26). */
  | 'encerrada';

/**
 * O TEXTO DA "GENÉRICA" — e por que ele mora aqui e não no banco.
 *
 * Dele, 11/09/2026: *"quem inventou genérica fui eu: para dizer um pedido de
 * autorização que não se enquadre em nenhum outro"*. Ou seja, ela é a AUSÊNCIA
 * de tipo (`fkey_tipo = NULL`, desde a migration 027) — e ausência não tem linha
 * na tabela de tipos para guardar a própria descrição.
 */
export const TIPO_GENERICA =
  'Não se encaixa em nenhum dos outros — não há tipo específico para este pedido.';

export interface TipoOpcao {
  id: number;
  nome: string;
  /** O QUE O TIPO QUER DIZER (097) — vira a explicação abaixo do select.
   *  Dele, 11/09: *"o que é cortesia? não sei. O que é compra? não lembro.
   *  Quem entra aí precisa saber dessas coisas"*. */
  descricao?: string | null;
  /** O comportamento é do TIPO, declarado no catálogo — a tela decide por
   *  estes dois o que mostrar, nunca por `nome === 'Desconto'`. */
  exige_cliente: boolean;
  pede_pedido_venda: boolean;
}

export interface LojaOpcao {
  id: number;
  nome: string;
  nome_curto?: string | null;
}

export interface UsuarioOpcao {
  id: number;
  nome: string;
  /** Já vem como URL (`/uploads/...`) ou nulo — a tela não monta caminho. */
  foto?: string | null;
  /** Quem autoriza (Vittorio e Adriana). Acompanha toda etapa por definição. */
  autorizador?: boolean;
}

export interface OSOpcao {
  id: number;
  codigo: string;
  cliente_nome?: string | null;
  /** Nome DIGITADO do fornecedor (099) — ainda não há cadastro. */
  fornecedor_nome?: string | null;
  cidade?: string | null;
}

export interface Autorizacao {
  id: number;
  tipo_id?: number | null;
  tipo_nome?: string | null;
  tipo_exige_cliente?: boolean;
  tipo_pede_pedido_venda?: boolean;
  loja_id?: number | null;
  loja_nome?: string | null;
  titulo: string;
  descricao?: string | null;
  /** As referências comerciais (078): como a loja fala do pedido enquanto ele
   *  não existe no Tiny. Continuam editáveis depois da decisão. */
  cliente_nome?: string | null;
  /** Nome DIGITADO do fornecedor (099) — ainda não há cadastro. */
  fornecedor_nome?: string | null;
  numero_orcamento?: number | null;
  numero_pedido_venda?: number | null;
  /** Autorizada, de tipo que pede pedido, ainda sem o número — a fila. */
  sem_pedido?: boolean;
  /** Tirar da frente (087): arquivada some da lista principal; o lixo some de
   *  tudo menos da lixeira. Nenhuma das duas mexe no status. */
  arquivada?: boolean;
  dt_arquivamento?: string | null;
  excluida?: boolean;
  dt_encerramento?: string | null;
  usuario_encerramento_nome?: string | null;
  /** O SEGUNDO EIXO (092): a decisão está em `status`, isto é o andamento. */
  encerrada?: boolean;
  andamento?: 'em_aberto' | 'encerrada';
  virou_venda?: boolean | null;
  valor?: number | null;
  percentual?: number | null;
  registrante_id?: number | null;
  registrante_nome?: string | null;
  solicitante_id?: number | null;
  solicitante_nome?: string | null;
  /** URL /uploads/... de quem pede. Ver a linha de autorizacao da Inbox. */
  solicitante_foto?: string | null;
  status: StatusAutorizacao;
  aprovador_id?: number | null;
  aprovador_nome?: string | null;
  decisao_obs?: string | null;
  obs_privada?: string | null; // só vem preenchido p/ donos/aprovadores
  dt_decisao?: string | null;
  entidade_tipo?: string | null;
  entidade_id?: number | null;
  entidade_label?: string | null; // ex.: código da OS
  qtd_mensagens: number;
  /** A estrela. É da LINHA, não de quem clicou — o pedido é um só e várias
   *  pessoas o veem, então marcar favorito marca para todos. Decisão escrita
   *  na migration 076, e ela cabe porque a fila tem duas pessoas. */
  favorito: boolean;
  criado_em?: string | null;
}

/** O detalhe não traz a conversa: ela vem do motor único, pelo componente. */
export type AutorizacaoDetalhe = Autorizacao;

export const STATUS: { value: StatusAutorizacao; label: string }[] = [
  { value: 'pendente', label: 'Pendente' },
  { value: 'autorizada', label: 'Autorizada' },
  { value: 'negada', label: 'Negada' },
  /* ⛔ "CANCELADA" AO LADO DE "NEGADA" LIA-SE COMO OUTRA RECUSA, e não é: é o
     solicitante tendo RETIRADO o pedido. Dele, 11/09: *"eu tenho autorizar e
     negar, cancelar autorização… eu mesmo não sei o que quer dizer"*. O valor no
     banco continua `cancelada` (nome de estado é para sempre — lei dos nomes);
     o que muda é a palavra na tela, que é onde a confusão morava. */
  { value: 'cancelada', label: 'Retirada' },
  { value: 'encerrada', label: 'Encerrada' },
];

/**
 * "R$ 4.800,00 · 15%" — o quanto do pedido, numa string.
 *
 * Estava TRIPLICADA quando veio para ca (30/08/2026): uma copia em
 * `page-completo.tsx`, outra em `page-celular.tsx`, e eu escrevi a terceira na
 * Inbox sem procurar. Tres copias da mesma frase e tres jeitos de o mesmo
 * pedido aparecer — que e exatamente o que ele apontou: *"esse nao e o mesmo
 * modelo que voce esta usando dentro da secao de autorizacoes; isso tem que ser
 * definido, tem que ser padronizado"*.
 *
 * Os DOIS quando ha os dois: um pedido de 7% sobre R$ 1.000 nao se resume a
 * "7%".
 */
export function valorResumo(a: Pick<Autorizacao, 'valor' | 'percentual'>): string {
  const partes: string[] = [];
  // `formatarDinheiro` de `@/lib/numero`, e nao um `toLocaleString` local: as
  // tres bordas de numero desta casa moram la (doc 07), e moeda escrita a mao
  // e como se juntam dois formatos de R$ no mesmo app.
  if (a.valor != null) partes.push(formatarDinheiro(Number(a.valor)));
  if (a.percentual != null) partes.push(`${Number(a.percentual)}%`);
  return partes.join(' \u00b7 ') || '\u2014';
}

/**
 * A ETAPA AINDA NÃO TERMINOU?
 *
 * ⚠️ Existe porque `estado === 'aberta'` espalhado pela tela virou SEIS bugs de
 * uma vez quando a 091 criou `em_andamento` — entre eles o chat que fechava ao
 * assumir a etapa, e um "Dispensada" escrito numa etapa que ninguém dispensou.
 *
 * A pergunta "acabou?" tem UM lugar. Quem quiser saber se é `aberta` mesmo
 * (para "Assumir", para a fila) compara com a string e sabe por quê.
 */
export const etapaAberta = (e: { estado: string }): boolean =>
  e.estado === 'aberta' || e.estado === 'em_andamento';

/**
 * ⛔ O TIPO DA ÂNCORA É `ordens_servico`, NUNCA `os` (migration 094).
 *
 * Regra 6 da reforma do banco: o valor é o nome do módulo por extenso. A sigla
 * custou caro aqui — o banco tinha os dois, TODA a leitura procurava `os`, e as
 * 17 autorizações gravadas certo não apareciam na OS a que pertenciam. Nada
 * quebrava: a aba só vinha vazia.
 *
 * A LEITURA aceita os dois (nome velho não se apaga); a ESCRITA usa a
 * constante.
 */
export const ANCORA_OS = 'ordens_servico';

export const eDaOS = (a: { entidade_tipo?: string | null }): boolean =>
  a.entidade_tipo === ANCORA_OS || a.entidade_tipo === 'os';

export const rotuloStatus = (s: StatusAutorizacao): string =>
  STATUS.find((x) => x.value === s)?.label ?? s;

/**
 * O SELO DO ANDAMENTO — o segundo eixo (migration 092).
 *
 * Dele, 01/09/2026: *"autorizada não é o fim — fica em aberto até alguém
 * informar o número do pedido de venda"*. Com um selo só, "Autorizada" parecia
 * fim de linha e "Encerrada" escondia a decisão, porque encerrar gravava por
 * cima dela.
 *
 * **Só faz sentido ao lado de uma AUTORIZADA.** Pendente ainda não foi
 * decidida, negada e cancelada já são fim: dizer "Em aberto" nas três seria
 * ruído, e ruído em selo é o que faz ninguém ler nenhum.
 */
export function andamentoDaAutorizacao(
  a: Pick<Autorizacao, 'status' | 'encerrada'>,
): { rotulo: string; cor: string } | null {
  if (a.status !== 'autorizada') return null;
  return a.encerrada
    ? { rotulo: 'Encerrada', cor: 'casaa-selo-neutro' }
    : { rotulo: 'Em aberto', cor: 'casaa-selo-info' };
}

export const varianteStatus = (s: StatusAutorizacao): string => {
  switch (s) {
    case 'pendente':
      return 'warning';
    case 'autorizada':
      return 'success';
    case 'negada':
      return 'destructive';
    case 'cancelada':
      return 'secondary';
    case 'encerrada':
      // Acabou bem e fechou: informação, não alerta — e diferente do verde
      // da "autorizada", que ainda espera o pedido.
      return 'info';
    default:
      return 'secondary';
  }
};

/**
 * Variante semantica -> classe do SELO DA CASA.
 *
 * NAO devolve mais `text-bg-*` do Bootstrap. Trocado em 30/08/2026, a pedido
 * dele: *"tem que definir os padroes para as cores dos labels, para eles nao
 * variarem conforme o tema - e ver como ficam no escuro"*.
 *
 * Status e INFORMACAO: a cor tem de ser nossa, fixa, e com o par claro/escuro
 * conferido. As classes vivem em `estilos/_selos.scss` e as cores em
 * `estilos/_cores.scss`. A assinatura ficou igual de proposito - as quatro
 * telas que chamam isto nao precisaram mudar uma linha.
 */
export function badge(v: string): string {
  const mapa: Record<string, string> = {
    primary: 'casaa-selo-destaque',
    info: 'casaa-selo-info',
    success: 'casaa-selo-sucesso',
    warning: 'casaa-selo-atencao',
    destructive: 'casaa-selo-perigo',
    secondary: 'casaa-selo-neutro',
  };
  return mapa[v] ?? 'casaa-selo-neutro';
}
